import { forwardRef, useId, type ReactNode } from 'react';
import { WarningCircleIcon } from '@phosphor-icons/react/dist/csr/WarningCircle';

import styles from './Field.module.css';

/**
 * Field — the label / hint / error chrome shared by Input, Select and Combobox.
 *
 * Not in the plan's file list. It exists because the three text-entry
 * primitives share an accessibility contract, not just a look: label
 * association, `aria-describedby` composition, `aria-required`, `aria-invalid`,
 * and the announcement route for a message that appears *after* focus has left
 * the field. Three copies of that is three chances to get it wrong, and the
 * copies would drift the first time one of them gained a hint.
 *
 * Four decisions worth reading before changing anything here.
 *
 * 1. **The error carries `role="alert"`, and that is load-bearing.** The
 *    handoff says "validate on blur, never on keystroke". By the time the
 *    message exists, focus has already moved to the next control — so
 *    `aria-describedby` alone would never be read out, because a description is
 *    announced when the described element receives focus. `role="alert"`
 *    announces the message where it is. Both are present: the live region for
 *    the moment it appears, the description for every later visit to the field.
 *
 * 2. **`aria-required`, not the native `required` attribute.** Native `required`
 *    hands validation to the browser: a native bubble on submit, in the
 *    browser's language, positioned by the browser, with none of the handoff's
 *    "describe the fix" copy. The handoff's form model is explicit — a `touched`
 *    set and an `errors` map validated on blur — so the constraint is announced
 *    and the enforcement stays in the application.
 *
 * 3. **The asterisk is `aria-hidden`.** It is a redundant visual for a fact
 *    `aria-required` already states. Left visible to assistive technology it
 *    renames the field to "Mobile number star".
 *
 * 4. **`width` is required, with no default.** The handoff calls a full-width
 *    input for a short value a bug. A component cannot know how long its
 *    content is, but a call site always does, so the type system asks. There is
 *    deliberately no `width="fill"` default — that is precisely the bug.
 */

/** Handoff, Forms 2: the field's inline size names the length of its content. */
export type FieldWidth = 'code' | 'compact' | 'phone' | 'fill';

const WIDTH_CLASS: Record<FieldWidth, string> = {
  code: styles['widthCode'] ?? '',
  compact: styles['widthCompact'] ?? '',
  phone: styles['widthPhone'] ?? '',
  fill: styles['widthFill'] ?? '',
};

/** What `Field` hands its control. Spread onto the input/select/combobox. */
export interface FieldControlProps {
  id: string;
  /** The control box: border, height, radius, ground — plus the invalid edge. */
  className: string;
  /** The content-length cap. Separate so a popover control can size a wrapper. */
  widthClassName: string;
  disabled: boolean | undefined;
  'aria-describedby': string | undefined;
  'aria-invalid': true | undefined;
  'aria-required': true | undefined;
}

export interface FieldProps {
  /** Visible label. Sits above the field; never beside it. */
  label: string;
  /** Handoff, Forms 2. Required on purpose — see the note above. */
  width: FieldWidth;
  /** Marks the field required to assistive tech and paints the asterisk. */
  required?: boolean;
  /** Describes the FIX, not the failure. Empty/undefined means valid. */
  error?: string;
  /** Standing help text. Rendered whether or not there is an error. */
  hint?: string;
  disabled?: boolean;
  /** Supplied by the control so label association survives a controlled id. */
  id?: string;
  className?: string;
  children: (control: FieldControlProps) => ReactNode;
}

export const Field = forwardRef<HTMLDivElement, FieldProps>(function Field(
  { label, width, required = false, error, hint, disabled = false, id, className, children },
  ref,
) {
  const generatedId = useId();
  const controlId = id ?? `${generatedId}field`;
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;

  const invalid = typeof error === 'string' && error !== '';
  const hasHint = typeof hint === 'string' && hint !== '';

  const describedBy =
    [hasHint ? hintId : null, invalid ? errorId : null].filter(Boolean).join(' ') || undefined;

  const control: FieldControlProps = {
    id: controlId,
    className: [styles['control'], invalid ? styles['controlInvalid'] : null]
      .filter(Boolean)
      .join(' '),
    widthClassName: WIDTH_CLASS[width],
    disabled: disabled || undefined,
    'aria-describedby': describedBy,
    'aria-invalid': invalid || undefined,
    'aria-required': required || undefined,
  };

  return (
    <div ref={ref} className={[styles['field'], className].filter(Boolean).join(' ')}>
      <label className={styles['label']} htmlFor={controlId}>
        {label}
        {required ? (
          <span className={styles['required']} aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      {children(control)}

      {hasHint ? (
        <p id={hintId} className={styles['hint']}>
          {hint}
        </p>
      ) : null}

      {invalid ? (
        <p id={errorId} className={styles['error']} role="alert">
          {/*
            Handoff: "ph-warning-circle". Decorative — the message beside it is
            the content, and an icon that reached the accessible name would read
            the error out as "warning circle Not a recognised code". `size` is
            passed explicitly because Phosphor emits no width/height without it.
          */}
          <span className={styles['errorIcon']} aria-hidden="true" data-testid="fieldErrorIcon">
            <WarningCircleIcon size={13} />
          </span>
          {error}
        </p>
      ) : null}
    </div>
  );
});
